import React, { useState } from "react";
import "./Signup.css";
import "../styles/shared-animations.css";
import { signupUser } from "../components/api";
import PrivacyPolicy from "./PrivacyPolicy";
import useTranslation from '../hooks/useTranslation';
import GlassInputWrapper from "../components/GlassInputWrapper";
import PasswordInput from "../components/PasswordInput";

export default function Signup({ onBack, onShowLogin }) {
  const selectedRole = localStorage.getItem('neuro_selected_role') || 'Individual';
  const [form, setForm] = useState({
    name: "",
    display_name: "",
    age: "",
    gender: "",
    password: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [pendingSignup, setPendingSignup] = useState(null);
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.password || !form.confirmPassword || form.password !== form.confirmPassword) {
      setMessage(t("Passwords do not match."));
      return;
    }

    try { 
      if (form.display_name) localStorage.setItem('neuro_display_name', form.display_name); 
    } catch { }

    setPendingSignup({
      name: form.name,
      display_name: form.display_name,
      password: form.password,
      age: form.age,
      gender: form.gender,
      role: selectedRole,
      info: "",
    });
    setShowPrivacy(true);
  };

  const handlePrivacyAgree = async () => {
    setShowPrivacy(false);
    if (!pendingSignup) return;
    try {
      const res = await signupUser(pendingSignup);
      setMessage(`${res.message}, ${res.username}! Redirecting to login...`);
      setSuccess(true);
      localStorage.setItem("neuro_selected_role", pendingSignup.role);
      setTimeout(() => {
        if (typeof onShowLogin === 'function') onShowLogin();
        if (typeof onBack === 'function') onBack();
      }, 2000);
    } catch (err) {
      setSuccess(false);
      setMessage(
        err.response?.data?.message || t("Signup failed. Please try again.")
      );
    }
    setPendingSignup(null);
  };

  const handlePrivacyDecline = () => {
    setShowPrivacy(false);
    setPendingSignup(null);
  };

  return (
    <>
      <div className="signup-modal-overlay">
        <div className="signup-modal-container">
          {/* Left Section - Form */}
          <section className="signup-modal-left">
            <button
              type="button"
              className="signup-close-btn"
              onClick={onBack}
              aria-label="Close Signup"
            >
              ×
            </button>
            
            <div className="signup-modal-content">
              <h1 className="signup-modal-title animate-element animate-delay-100">
                {t('Welcome to Neurolonic!')}
              </h1>
              
              <p className="signup-modal-description animate-element animate-delay-200">
                {t('Create your account and start your journey with us')}
              </p>

              <form className="signup-modal-form" onSubmit={handleSubmit}>
                <div className="signup-selected-role animate-element animate-delay-300">
                  {t(selectedRole)}
                </div>

                <div className="form-group-modal animate-element animate-delay-400">
                  <label className="form-label-modal">
                    {t('Name')}
                  </label>
                  <GlassInputWrapper>
                    <input
                      name="display_name"
                      type="text"
                      autoComplete="off"
                      value={form.display_name}
                      onChange={handleChange}
                      className="form-input-modal"
                      placeholder={t('Enter your name')}
                    />
                  </GlassInputWrapper>
                </div>

                <div className="form-group-modal animate-element animate-delay-500">
                  <label className="form-label-modal">
                    {t('Email')}
                  </label>
                  <GlassInputWrapper>
                    <input
                      name="name"
                      type="email"
                      autoComplete="off"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="form-input-modal"
                      placeholder={t('Enter your email')}
                    />
                  </GlassInputWrapper>
                </div>

                <div className="signup-row animate-element animate-delay-600">
                  <div className="signup-col">
                    <label className="form-label-modal">
                      {t('Age')}
                    </label>
                    <GlassInputWrapper>
                      <input
                        name="age"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        minLength={1}
                        required
                        value={form.age}
                        onChange={handleChange}
                        className="form-input-modal"
                        placeholder={t('Age')}
                      />
                    </GlassInputWrapper>
                  </div>
                  <div className="signup-col">
                    <label className="form-label-modal">
                      {t('Sex')}
                    </label>
                    <GlassInputWrapper>
                      <select
                        name="gender"
                        required
                        value={form.gender}
                        onChange={handleChange}
                        className="form-input-modal form-select"
                      >
                        <option value="">{t('Select')}</option>
                        <option value="Male">{t('Male')}</option>
                        <option value="Female">{t('Female')}</option>
                        <option value="Intersex / Other">{t('Intersex / Other')}</option>
                      </select>
                    </GlassInputWrapper>
                  </div>
                </div>

                <div className="signup-row animate-element animate-delay-700">
                  <div className="signup-col">
                    <label className="form-label-modal">
                      {t('Create Password')}
                    </label>
                    <GlassInputWrapper>
                      <PasswordInput
                        name="password"
                        required
                        value={form.password}
                        onChange={handleChange}
                        placeholder={t('Create password')}
                        autoComplete="new-password"
                      />
                    </GlassInputWrapper>
                  </div>
                  <div className="signup-col">
                    <label className="form-label-modal">
                      {t('Confirm Password')}
                    </label>
                    <GlassInputWrapper>
                      <PasswordInput
                        name="confirmPassword"
                        required
                        value={form.confirmPassword}
                        onChange={handleChange}
                        placeholder={t('Confirm password')}
                        autoComplete="new-password"
                      />
                    </GlassInputWrapper>
                  </div>
                </div>

                {message && (
                  <div className={`signup-message animate-element ${success ? "success" : "error"}`}>
                    {message}
                  </div>
                )}

                <button 
                  className="signup-btn-modal submit animate-element animate-delay-800" 
                  type="submit"
                >
                  {t('Sign Up')}
                </button>

                <div className="signup-login-modal animate-element animate-delay-900">
                  {t('Already have an account?')} {' '}
                  <button 
                    type="button"
                    className="signup-login-link"
                    onClick={e => { 
                      e.preventDefault(); 
                      if (onShowLogin) { 
                        onShowLogin(); 
                        if (onBack) onBack(); 
                      } 
                    }}
                  >
                    {t('Log in now')}
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* Right Section - Hero Image */}
          <section className="signup-modal-right">
            <div 
              className="signup-modal-hero-image animate-slide-right animate-delay-300"
              style={{ 
                backgroundImage: `url(https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=2160&q=80)` 
              }}
            ></div>
          </section>
        </div>
      </div>
      
      <PrivacyPolicy
        show={showPrivacy}
        onAgree={handlePrivacyAgree}
        onDecline={handlePrivacyDecline}
      />
    </>
  );
}
